import React from 'react';
import {
  TextField,
  Divider,
  MenuItem,
  Select,
  InputAdornment,
} from '@mui/material';
import moment from 'moment/moment';
import { ButtonClassic } from '../../ReusableComponents/NewDesign/Buttons/Buttons';
import { ItemLeadTime } from '../types';
import countries from '../../../Lib/Constants/countries';
import {
  exclusivityOptions,
  shipmentModeOptions,
} from './RequestedItemTableRowEditable';
import styles from './ColumnForm.module.css';

type BaseFormProps = {
  children: React.ReactNode;
  fieldName: string;
  onSave: () => void;
  onCancel: () => void;
};

function BaseForm({ children, fieldName, onSave, onCancel }: BaseFormProps) {
  const handleSave = (e: React.MouseEvent) => {
    e.preventDefault();
    onSave();
    onCancel();
  };

  return (
    <form className={styles.columnForm}>
      <h6 className={styles.columnFormTitle}>Bulk Apply</h6>
      <p>{fieldName}</p>
      {children}
      <Divider />
      <div>
        <ButtonClassic onClick={handleSave}>Apply to all</ButtonClassic>
      </div>
    </form>
  );
}

type FormProps<C extends keyof ItemLeadTime> = {
  onSave: (column: C, value: ItemLeadTime[C]) => void;
  onCancel: () => void;
  domesticEligible?: boolean;
};

export function FormCoo({
  onSave,
  onCancel,
  domesticEligible,
}: FormProps<'country_of_origin'>) {
  const [value, setValue] = React.useState('');

  return (
    <BaseForm
      fieldName="COO"
      onSave={() => onSave('country_of_origin', value)}
      onCancel={onCancel}
    >
      <Select
        style={{ width: '100%' }}
        value={value}
        onChange={(e) => setValue(e.target.value)}
      >
        {countries.map((country) => (
          <MenuItem
            key={country.text}
            value={country.text}
            disabled={!domesticEligible && country.value === 'US'}
          >
            {country.text}
          </MenuItem>
        ))}
      </Select>
    </BaseForm>
  );
}

export function FormExclusivity({
  onSave,
  onCancel,
}: FormProps<'exclusivity'>) {
  const [value, setValue] = React.useState('Not Exclusive');

  return (
    <BaseForm
      fieldName="Exclusivity"
      onSave={() => onSave('exclusivity', value)}
      onCancel={onCancel}
    >
      <Select
        style={{ width: '100%' }}
        value={value}
        onChange={(e) => setValue(e.target.value)}
      >
        {exclusivityOptions.map(({ value: item, text }) => (
          <MenuItem key={item} value={item}>
            {text}
          </MenuItem>
        ))}
      </Select>
    </BaseForm>
  );
}

export function FormShipmentMode({
  onSave,
  onCancel,
}: FormProps<'shipment_mode'>) {
  const [value, setValue] = React.useState('');

  return (
    <BaseForm
      fieldName="Shipment Mode"
      onSave={() => onSave('shipment_mode', value)}
      onCancel={onCancel}
    >
      <Select
        style={{ width: '100%' }}
        value={value}
        onChange={(e) => setValue(e.target.value)}
      >
        {shipmentModeOptions.map(({ value: item, text }) => (
          <MenuItem key={item} value={item}>
            {text}
          </MenuItem>
        ))}
      </Select>
    </BaseForm>
  );
}

export function FormEarliestCustomCut({
  onSave,
  onCancel,
}: FormProps<'earliest_custom_cut_delivery_date'>) {
  const [value, setValue] = React.useState('');

  return (
    <BaseForm
      fieldName="Earliest Custom Cut Delivery Date"
      onSave={() => {
        onSave('earliest_custom_cut_delivery_date', value);
      }}
      onCancel={onCancel}
    >
      <TextField
        variant="standard"
        type="date"
        value={value}
        inputProps={{
          min: moment().format('YYYY-MM-DD'),
          onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
            setValue(e.target.value),
        }}
      />
    </BaseForm>
  );
}

export function FormCustomCutLeadTime({
  onSave,
  onCancel,
}: FormProps<'custom_cut_lead_time_in_weeks'>) {
  const [value, setValue] = React.useState(0);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue(Number(e.target.value) ?? 0);
  };

  return (
    <BaseForm
      fieldName="Custom Cut Lead Time in Weeks"
      onSave={() => {
        onSave('custom_cut_lead_time_in_weeks', value);
      }}
      onCancel={onCancel}
    >
      <TextField
        variant="standard"
        value={value}
        InputProps={{
          endAdornment: <InputAdornment position="end">weeks</InputAdornment>,
        }}
        onChange={handleChange}
      />
    </BaseForm>
  );
}

export function FormStockQuantity({
  onSave,
  onCancel,
}: FormProps<'stock_quantity'>) {
  const [value, setValue] = React.useState(0);

  return (
    <BaseForm
      fieldName="Stock Qty"
      onSave={() => onSave('stock_quantity', value)}
      onCancel={onCancel}
    >
      <TextField
        variant="outlined"
        value={value}
        onChange={(e) => setValue(Number(e.target.value) || 0)}
      />
    </BaseForm>
  );
}

export function FormStockRatio({ onSave, onCancel }: FormProps<'stock_ratio'>) {
  const [value, setValue] = React.useState('');

  return (
    <BaseForm
      fieldName="Stock Ratio"
      onSave={() => onSave('stock_ratio', value)}
      onCancel={onCancel}
    >
      <TextField
        variant="outlined"
        value={value}
        onChange={(e) => setValue(e.target.value)}
      />
    </BaseForm>
  );
}

export function FormEarliestIHD({
  onSave,
  onCancel,
}: FormProps<'earliest_IHD_date_to_deliver_stock'>) {
  const [value, setValue] = React.useState('');

  return (
    <BaseForm
      fieldName="Earliest IHD Date to Deliver Stock"
      onSave={() => onSave('earliest_IHD_date_to_deliver_stock', value)}
      onCancel={onCancel}
    >
      <TextField
        variant="standard"
        type="date"
        value={value}
        inputProps={{
          min: moment().format('YYYY-MM-DD'),
          onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
            setValue(e.target.value),
        }}
      />
    </BaseForm>
  );
}

export function FormIncomingStockQuantity({
  onSave,
  onCancel,
}: FormProps<'incoming_stock_quantity'>) {
  const [value, setValue] = React.useState(0);

  return (
    <BaseForm
      fieldName="Incoming Stock Quantity"
      onSave={() => onSave('incoming_stock_quantity', value)}
      onCancel={onCancel}
    >
      <TextField
        variant="outlined"
        value={value}
        onChange={(e) => setValue(Number(e.target.value) || 0)}
      />
    </BaseForm>
  );
}

export function FormIncomingStockDate({
  onSave,
  onCancel,
}: FormProps<'incoming_stock_date'>) {
  const [value, setValue] = React.useState('');

  return (
    <BaseForm
      fieldName="Incoming Stock Date"
      onSave={() => onSave('incoming_stock_date', value)}
      onCancel={onCancel}
    >
      <TextField
        variant="standard"
        type="date"
        value={value}
        inputProps={{
          min: moment().format('YYYY-MM-DD'),
          onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
            setValue(e.target.value),
        }}
      />
    </BaseForm>
  );
}

export function FormIncomingStockRatio({
  onSave,
  onCancel,
}: FormProps<'incoming_stock_ratio'>) {
  const [value, setValue] = React.useState('');

  return (
    <BaseForm
      fieldName="Incoming Stock Ratio"
      onSave={() => onSave('incoming_stock_ratio', value)}
      onCancel={onCancel}
    >
      <TextField
        variant="outlined"
        value={value}
        onChange={(e) => setValue(e.target.value)}
      />
    </BaseForm>
  );
}

export function FormCustomMOQJr({
  onSave,
  onCancel,
}: FormProps<'custom_MOQ_jr'>) {
  const [value, setValue] = React.useState(0);

  return (
    <BaseForm
      fieldName="Custom MOQ Jr"
      onSave={() => onSave('custom_MOQ_jr', value)}
      onCancel={onCancel}
    >
      <TextField
        variant="outlined"
        value={value}
        onChange={(e) => setValue(Number(e.target.value) || 0)}
      />
    </BaseForm>
  );
}

export function FormCustomMOQPlus({
  onSave,
  onCancel,
}: FormProps<'custom_MOQ_plus'>) {
  const [value, setValue] = React.useState(0);

  return (
    <BaseForm
      fieldName="Custom MOQ Plus"
      onSave={() => onSave('custom_MOQ_plus', value)}
      onCancel={onCancel}
    >
      <TextField
        variant="outlined"
        value={value}
        onChange={(e) => setValue(Number(e.target.value) || 0)}
      />
    </BaseForm>
  );
}

export function FormComments({ onSave, onCancel }: FormProps<'comments'>) {
  const [value, setValue] = React.useState('');

  return (
    <BaseForm
      fieldName="Other comments"
      onSave={() => onSave('comments', value)}
      onCancel={onCancel}
    >
      <TextField
        variant="outlined"
        value={value}
        onChange={(e) => setValue(e.target.value)}
      />
    </BaseForm>
  );
}
